import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import {
  FileText,
  Download,
  Maximize2,
  Minimize2,
  Receipt,
  CreditCard,
} from "lucide-react";

const Invoice = ({ studentId: propStudentId }) => {
  const { studentId: paramStudentId } = useParams();
  const studentId = propStudentId || paramStudentId;

  const [invoiceUrl, setInvoiceUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);

  /* =========================
     FETCH INVOICE
  ========================== */
  const fetchInvoicePDF = async () => {
    if (!studentId) return;
    setLoading(true);

    try {
      const response = await axios.get(
        `https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/Invoice/view/${studentId}`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      setInvoiceUrl(URL.createObjectURL(blob));
    } catch (error) {
      console.error("Failed to load invoice:", error);
      setInvoiceUrl(null);
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     DOWNLOAD
  ========================== */
  const handleDownload = () => {
    if (!invoiceUrl) return;
    const link = document.createElement("a");
    link.href = invoiceUrl;
    link.download = `Invoice_${studentId}.pdf`;
    link.click();
  };

  useEffect(() => {
    fetchInvoicePDF();

    return () => {
      if (invoiceUrl) URL.revokeObjectURL(invoiceUrl);
    };
  }, [studentId]);

  /* =========================
     UI
  ========================== */
  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header Banner - Matching Dashboard */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-blue-700 to-gray-800 p-6 md:p-8 text-white shadow-xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-white/20 backdrop-blur rounded-xl">
              <CreditCard className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Invoice Management</h1>
              <p className="text-blue-100 text-sm mt-1">View and download your generated student invoice</p>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Viewer Card */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-900 rounded-lg">
              <Receipt className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Invoice Document</h2>
              <p className="text-sm text-gray-500">Review your invoice details below</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 relative">
          {loading && (
            <div className="flex flex-col items-center justify-center h-80">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-4" />
              <p className="text-sm text-gray-500 font-medium">Loading invoice document...</p>
              <p className="text-xs text-gray-400 mt-1">Please wait while we prepare your invoice</p>
            </div>
          )}

          {!loading && invoiceUrl && (
            <div className="relative">
              {/* Action Buttons - Matching Dashboard Button Styles */}
              <div className="absolute top-4 right-4 flex gap-3 z-20">
                <button
                  onClick={() => setIsFullScreen(!isFullScreen)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-gray-900 text-white hover:bg-gray-800 transition-all duration-200 shadow-md"
                >
                  {isFullScreen ? (
                    <Minimize2 className="w-4 h-4" />
                  ) : (
                    <Maximize2 className="w-4 h-4" />
                  )}
                  {isFullScreen ? "Exit Fullscreen" : "Fullscreen"}
                </button>

                <button
                  onClick={handleDownload}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-green-600 text-white hover:bg-green-700 transition-all duration-200 shadow-md"
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </button>
              </div>

              {/* PDF Viewer - Balanced Height */}
              <iframe
                src={invoiceUrl}
                title="Student Invoice"
                className={`w-full bg-white transition-all duration-300 ${
                  isFullScreen
                    ? "fixed inset-0 z-50 h-screen"
                    : "h-[650px]"
                }`}
              />
            </div>
          )}

          {!loading && !invoiceUrl && (
            <div className="flex flex-col items-center justify-center h-80">
              <div className="p-4 bg-gray-100 rounded-full mb-4">
                <FileText className="w-12 h-12 text-gray-400" />
              </div>
              <p className="text-gray-500 font-medium">No Invoice Generated</p>
              <p className="text-sm text-gray-400 mt-1">The invoice will appear here once it has been created</p>
              <p className="text-xs text-gray-400 mt-2">Please contact administration if you believe this is an error</p>
            </div>
          )}
        </div>
      </div>

      {/* Information Notice - Matching Dashboard card style */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Receipt className="w-4 h-4 text-blue-600" />
            </div>
            <span>Invoice generated upon lease finalization</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-1.5 bg-gray-300 rounded-full" />
            <span>Download for your records</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-1.5 bg-gray-300 rounded-full" />
            <span>Contact Residence Manager for inquiries</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoice;